import instance, { ADMIN_PREFIX } from 'services/api';
import {
  AdminPaymentAppointmentInput,
  AppointmentDateInput,
  AppointmentDetail,
  AppointmentDoctorPaymentInput,
  AppointmentFile,
  AppointmentFileInput,
  AppointmentListQuery,
  AppointmentListResponse,
  AppointmentListRow,
  AppointmentNoteInput,
  AppointmentPaymentInfoResult,
  AppointmentRejectInput,
  AppointmentState,
  AppointmentStatus,
  CreateAppointmentInput,
  OpenRoomResult
} from 'types/api/appointments';

interface PaginatedEnvelope<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * GET /api/v2/admin/appointments — paginated list with status / name /
 * plan_type / admin_paid filters and org-scoping via the organization
 * header. BE returns the PaginatedResponse envelope (1-based page);
 * service coerces to the legacy {results,total} shape (0-based) the
 * FE Appointments page + Dashboard widget consume.
 */
export const fetchAppointments = async (
  query: AppointmentListQuery
): Promise<AppointmentListResponse> => {
  const params: Record<string, string | number | boolean> = {
    page: query.page ?? 1,
    limit: query.limit ?? 10
  };

  if (query.status !== undefined) params.status = query.status;
  if (query.name) params.name = query.name;
  if (query.plan_type !== undefined) params.plan_type = query.plan_type;
  if (query.admin_paid !== undefined) params.admin_paid = query.admin_paid;
  if (query.doctor_id !== undefined) params.doctorId = query.doctor_id;
  if (query.patient_id !== undefined) params.patientId = query.patient_id;
  if (query.creator !== undefined) params.creator = query.creator;
  if (query.paid !== undefined) params.paid = query.paid;

  const res = await instance.get<PaginatedEnvelope<AppointmentListRow>>(
    `${ADMIN_PREFIX}/appointments`,
    { params }
  );

  return {
    results: res.data.data ?? [],
    total: res.data.total ?? 0
  };
};

/**
 * GET /api/v2/admin/appointments — paginated list with status / name /
 * plan_type / admin_paid filters and org-scoping via the organization
 * header. BE returns the PaginatedResponse envelope (1-based page);
 * service coerces to the legacy {results,total} shape (0-based) the
 * FE Appointments page + Dashboard widget consume.
 */
export const fetchAdminCreator = async (
  query: AppointmentListQuery,
  isFree = false
): Promise<AppointmentListResponse> => {
  const params: Record<string, string | number | boolean> = {
    page: query.page ?? 1,
    limit: query.limit ?? 10
  };

  if (isFree) params.isFree = isFree;

  const res = await instance.get<PaginatedEnvelope<AppointmentListRow>>(
    `${ADMIN_PREFIX}/appointments/creator`,
    { params }
  );

  return {
    results: res.data.data ?? [],
    total: res.data.total ?? 0
  };
};

/**
 * GET /api/v2/admin/appointments/:id — detail row hydrated via
 * projectAppointmentDetail (cycle-25 closure): superset of the list
 * shape adding admin_notes / admin_payment / report / reports_file /
 * files / notes + extra patient/doctor scalars + plan.description.
 */
export const fetchAppointmentById = async (
  id: string | number
): Promise<AppointmentDetail> => {
  const res = await instance.get<AppointmentDetail>(
    `${ADMIN_PREFIX}/appointments/${id}`
  );

  return res.data;
};

/**
 * PUT /api/v2/admin/appointments/:id/status (cycle-24).
 */
export const updateAppointmentStatus = (
  id: string | number,
  status: AppointmentStatus
): Promise<unknown> =>
  instance.put(`${ADMIN_PREFIX}/appointments/${id}/status`, { status });

/**
 * PUT /api/v2/admin/appointments/:id/state (cycle-24). admin_note
 * is the audit-trail note recorded alongside the state flip.
 */
export const updateAppointmentState = (
  id: string | number,
  state: AppointmentState,
  admin_note?: string
): Promise<unknown> =>
  instance.put(`${ADMIN_PREFIX}/appointments/${id}/state`, {
    state,
    admin_note
  });

/**
 * POST /api/v2/admin/appointments/:id/notes — CREATE_APPOINTMENT_NOTE.
 */
export const createAppointmentNote = (
  id: string | number,
  input: AppointmentNoteInput
): Promise<unknown> =>
  instance.post(`${ADMIN_PREFIX}/appointments/${id}/notes`, input);

/**
 * POST /api/v2/admin/appointments/:id/files — CREATE_APPOINTMENT_FILE.
 */
export const createAppointmentFile = (
  id: string | number,
  input: AppointmentFileInput
): Promise<unknown> =>
  instance.post(`${ADMIN_PREFIX}/appointments/${id}/files`, input);

/**
 * POST /api/v2/admin/appointments/:id/dates — REQUEST_APPOINTMENT_DATES.
 * Inserts a proposed date row (accepted=false).
 */
export const createAppointmentDate = (
  id: string | number,
  input: AppointmentDateInput
): Promise<unknown> =>
  instance.post(`${ADMIN_PREFIX}/appointments/${id}/dates`, input);

/**
 * POST /api/v2/admin/appointments/:id/reject — REJECT_APPOINTMENT.
 * Admin-side status flip + audit note; distinct from DELETE /:id
 * (force-cancel) which carries refund-eligible semantics.
 */
export const rejectAppointment = (
  id: string | number,
  input: AppointmentRejectInput
): Promise<unknown> =>
  instance.post(`${ADMIN_PREFIX}/appointments/${id}/reject`, input);

/**
 * POST /api/v2/admin/appointments/:id/open-room — OPEN_ROOM.
 * Returns the meeting redirect_url + meetUrl pair.
 */
export const openAppointmentRoom = async (
  id: string | number
): Promise<OpenRoomResult> => {
  const res = await instance.post<OpenRoomResult>(
    `${ADMIN_PREFIX}/appointments/${id}/open-room`,
    {}
  );

  return res.data;
};

/**
 * POST /api/v2/admin/appointments/:id/doctor-payment —
 * UPDATE_DOCTOR_PAYMENT. Writes the appointment_payment_info row
 * recording that the doctor payout has been processed.
 */
export const updateAppointmentDoctorPayment = (
  id: string | number,
  input: AppointmentDoctorPaymentInput
): Promise<unknown> =>
  instance.post(`${ADMIN_PREFIX}/appointments/${id}/doctor-payment`, input);

/**
 * PUT /api/v2/admin/appointments/dates/:dateId — admin reschedule.
 */
export const updateAppointmentApprovedDate = (
  dateId: string | number,
  body: { date: string; confirm?: boolean }
): Promise<unknown> =>
  instance.put(`${ADMIN_PREFIX}/appointments/dates/${dateId}`, body);

/**
 * POST /api/v2/admin/appointments — admin create (cycle-26 +
 * cycle-29). Optional patient_id / doctor_id / date support the
 * legacy "Sessions/Free" empty-body create (placeholder admin call).
 * BE transactionally inserts the Appointment row + (when date is
 * present) seeds an AppointmentDate (accepted=true), optional
 * AppointmentNote, and AppointmentFile per files[] entry. Returns
 * the full detail-shape payload.
 */
export const createAppointment = async (
  input: CreateAppointmentInput
): Promise<AppointmentDetail> => {
  const res = await instance.post<AppointmentDetail>(
    `${ADMIN_PREFIX}/appointments`,
    input
  );

  return res.data;
};

/**
 * POST /api/v2/admin/appointments/admin-payment (cycle-24) — admin
 * records an off-platform paid session. Minimal {price, reason} body
 * supported (cycle-29 confirmation).
 */
export const createAdminPaymentAppointment = async (
  input: AdminPaymentAppointmentInput
): Promise<AppointmentDetail> => {
  const res = await instance.post<AppointmentDetail>(
    `${ADMIN_PREFIX}/appointments/admin-payment`,
    input
  );

  return res.data;
};

/**
 * DELETE /api/v2/admin/appointments/:id (cycle-24) — admin
 * force-cancel. Re-exported alias of force-cancel for Sessions/Paid.
 */
export const deleteAppointment = async (
  id: string | number
): Promise<unknown> => {
  const res = await instance.delete(`${ADMIN_PREFIX}/appointments/${id}`);

  return res.data;
};

/**
 * GET /api/v2/admin/appointments/:id/previous-files (cycle-25).
 * AppointmentFiles from any OTHER appointment of the same patient.
 */
export const fetchAppointmentPreviousFiles = async (
  id: string | number
): Promise<AppointmentFile[]> => {
  const res = await instance.get<AppointmentFile[]>(
    `${ADMIN_PREFIX}/appointments/${id}/previous-files`
  );

  return res.data ?? [];
};

/**
 * GET /api/v2/admin/appointments/:id/payment-info (cycle-36 closure).
 * Slim popover source — { id, patient_payment_info[] } only.
 * Replaces the previous pattern of pulling the full AppointmentDetail
 * just to read .patient_payment_info on the Sessions/Paid popover.
 */
export const fetchAppointmentPaymentInfo = async (
  id: string | number
): Promise<AppointmentPaymentInfoResult> => {
  const res = await instance.get<AppointmentPaymentInfoResult>(
    `${ADMIN_PREFIX}/appointments/${id}/payment-info`
  );

  return res.data;
};
